/**
 * DoubleWeekView Component
 *
 * View component for 14-day mode in the Sales Timeline.
 * Displays two stacked WeekCharts with a shared legend.
 *
 * US-PE-006: Sales Timeline - Phase 3 Page Components
 */

import WeekChart from './WeekChart'
import ChartLegend from './ChartLegend'
import { formatWeekLabel } from '@/utils/timeline-utils'
import { createDate } from '@/utils/dateUtils'

/**
 * Get week label from week data
 *
 * @param {Object} weekData - Week data object with weekStart and items
 * @returns {string} Formatted week label
 */
function getWeekLabel(weekData) {
  if (!weekData?.weekStart) return ''

  const start = weekData.weekStart
  const end = createDate(start.getTime())
  end.setDate(end.getDate() + 6)

  return formatWeekLabel(start, end)
}

/**
 * DoubleWeekView - Two weeks stacked chart view
 *
 * @param {Object} props
 * @param {Array} props.weeklyData - Array of week data objects from useTimelineData
 *   Each: { weekStart: Date, items: Array of daily data }
 * @param {Array} props.strains - Array of strain objects { id, name, color }
 * @param {boolean} [props.showAllStrains=true] - Show all strains stacked or single strain
 * @param {string|null} [props.selectedStrainId] - Selected strain ID for single strain view
 * @param {Object} [props.dateRange] - Date range for the view { start, end }
 * @param {string} [props.className] - Additional CSS classes
 */
export default function DoubleWeekView({
  weeklyData = [],
  strains = [],
  showAllStrains = true,
  selectedStrainId = null,
  className = ''
}) {
  // Get the first two weeks of data
  const week1 = weeklyData[0]
  const week2 = weeklyData[1]

  // Collect all strains that have data across both weeks
  const allWeekItems = [...(week1?.items || []), ...(week2?.items || [])]
  const strainsWithData = strains.filter(strain => {
    return allWeekItems.some(item =>
      item.supplyByStrain && item.supplyByStrain[strain.name] > 0
    )
  })

  // Handle empty data
  if (!week1 && !week2) {
    return (
      <div className={`${className}`}>
        <div className="bg-slate-50 rounded-lg border border-dashed border-slate-200 p-8 text-center">
          <span className="text-sm text-slate-500">No data for this period</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Shared Legend */}
      {showAllStrains && strainsWithData.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
          <ChartLegend
            strains={strainsWithData}
            showDemandLine={true}
          />
        </div>
      )}

      {/* Stacked Week Charts */}
      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
        {/* Week 1 */}
        {week1 && (
          <div className="p-4">
            <WeekChart
              data={week1.items}
              strains={strains}
              showAllStrains={showAllStrains}
              selectedStrainId={selectedStrainId}
              weekLabel={getWeekLabel(week1)}
              height={180}
            />
          </div>
        )}

        {/* Week 2 */}
        {week2 && (
          <div className="p-4">
            <WeekChart
              data={week2.items}
              strains={strains}
              showAllStrains={showAllStrains}
              selectedStrainId={selectedStrainId}
              weekLabel={getWeekLabel(week2)}
              height={180}
            />
          </div>
        )}
      </div>
    </div>
  )
}
